import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBell, FiX, FiFilm } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

const NotificationBar = () => {
  const { notifications, user } = useAuth();
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cs_dismissed') || '[]'); }
    catch { return []; }
  });
  const [panelOpen, setPanelOpen] = useState(false);

  const unseen = notifications.filter(n => !dismissed.includes(n.id));

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem('cs_dismissed', JSON.stringify(next));
  };

  const dismissAll = () => {
    const ids = notifications.map(n => n.id);
    const next = [...new Set([...dismissed, ...ids])];
    setDismissed(next);
    localStorage.setItem('cs_dismissed', JSON.stringify(next));
    setPanelOpen(false);
  };

  if (!user || notifications.length === 0) return null;

  return (
    <>
      {/* Bell button fixed bottom-right */}
      <motion.button
        initial={{ scale: 0 }} animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
        onClick={() => setPanelOpen(o => !o)}
        className="fixed bottom-6 right-6 z-40 w-13 h-13 rounded-full flex items-center justify-center shadow-2xl"
        style={{
          background: 'var(--red)',
          width: 52, height: 52,
          boxShadow: '0 0 30px rgba(229,9,20,0.5)',
        }}
      >
        <FiBell size={22} color="#fff" />
        {unseen.length > 0 && (
          <motion.span
            initial={{ scale: 0 }} animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center font-accent"
            style={{ background: '#F5C518', color: '#000', fontSize: 10, fontWeight: 700 }}
          >
            {unseen.length > 9 ? '9+' : unseen.length}
          </motion.span>
        )}
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {panelOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40" onClick={() => setPanelOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 340, damping: 28 }}
              className="fixed bottom-20 right-6 z-50 w-80 rounded-2xl overflow-hidden shadow-2xl"
              style={{ background: 'rgba(13,13,20,0.99)', border: '1px solid rgba(255,255,255,0.08)' }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
                <div className="flex items-center gap-2">
                  <FiBell size={16} style={{ color: 'var(--red)' }} />
                  <span className="font-display text-lg tracking-wide" style={{ color: 'var(--text)' }}>Notifications</span>
                  {unseen.length > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-accent"
                      style={{ background: 'rgba(229,9,20,0.2)', color: 'var(--red)' }}>
                      {unseen.length} new
                    </span>
                  )}
                </div>
                {notifications.length > 0 && (
                  <button onClick={dismissAll} className="text-xs font-body hover:text-white transition-colors"
                    style={{ color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    Clear all
                  </button>
                )}
              </div>

              {/* List */}
              <div className="max-h-80 overflow-y-auto hide-scrollbar">
                <AnimatePresence>
                  {notifications.map(n => (
                    <motion.div key={n.id} layout
                      initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="flex items-start gap-3 px-5 py-3.5 border-b group"
                      style={{
                        borderColor: 'rgba(255,255,255,0.04)',
                        background: dismissed.includes(n.id) ? 'transparent' : 'rgba(229,9,20,0.04)',
                      }}
                    >
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: dismissed.includes(n.id) ? 'rgba(255,255,255,0.05)' : 'rgba(229,9,20,0.15)' }}>
                        <FiFilm size={14} style={{ color: dismissed.includes(n.id) ? 'var(--muted)' : 'var(--red)' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-body font-medium leading-snug" style={{ color: 'var(--text)' }}>{n.title}</p>
                        {n.body && <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--muted)' }}>{n.body}</p>}
                        {n.createdAt && (
                          <p className="text-xs mt-1 font-accent" style={{ color: 'var(--muted)', opacity: 0.6 }}>
                            {new Date(n.createdAt.seconds * 1000).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      {!dismissed.includes(n.id) && (
                        <button onClick={() => dismiss(n.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          style={{ color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
                          <FiX size={14} />
                        </button>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default NotificationBar;
